import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Crown, Sparkles, FileText, Mail, ChevronRight, Shield, CreditCard, Loader2 } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { toast } from "sonner";

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);

  const isPaid = user?.plan && user.plan !== "free";

  const openBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await client.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open billing portal");
      setPortalLoading(false);
    }
  };

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  const initial = (user?.name || "U")[0].toUpperCase();

  return (
    <AppShell title="Profile">
      <div className="flex items-center gap-4 rounded-[28px] border border-black/5 bg-white p-5">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <div className="grid place-items-center w-16 h-16 rounded-full bg-ho-primary/10 font-display text-2xl font-extrabold text-ho-primary">
            {initial}
          </div>
        )}
        <div className="min-w-0">
          <div className="truncate font-display text-xl font-bold">{user?.name}</div>
          <div className="truncate text-sm text-ho-muted">{user?.email}</div>
        </div>
      </div>

      {/* Plan card */}
      <div className="mt-4 overflow-hidden rounded-[28px] bg-ho-ink p-5 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-400" />
            <span className="font-display text-lg font-bold capitalize">{user?.plan || "Free"} plan</span>
          </div>
          <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">Active</span>
        </div>
        <p className="mt-2 text-sm text-white/60">
          {isPaid
            ? "Manage your subscription, update your card, or view invoices anytime."
            : "Upgrade to publish unlimited sites and unlock custom domains."}
        </p>
        {isPaid ? (
          <button onClick={openBilling} disabled={portalLoading} data-testid="profile-manage-billing"
            className="tap-scale mt-4 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-ho-ink disabled:opacity-60">
            {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 text-ho-primary" />}
            {portalLoading ? "Opening…" : "Manage billing"}
          </button>
        ) : (
          <button onClick={() => navigate("/pricing")} data-testid="profile-upgrade"
            className="tap-scale mt-4 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-ho-ink">
            <Sparkles className="w-4 h-4 text-ho-primary" /> See plans
          </button>
        )}
      </div>

      {/* Links */}
      <div className="mt-4 overflow-hidden rounded-[28px] border border-black/5 bg-white">
        <Row icon={Sparkles} label="Create a new site" onClick={() => navigate("/app/templates")} testid="profile-create" />
        <Row icon={FileText} label="Read the blog" onClick={() => navigate("/blog")} testid="profile-blog" />
        <Row icon={Mail} label="Contact support" onClick={() => navigate("/contact")} testid="profile-contact" />
        <Row icon={Shield} label="Why phone-first" onClick={() => navigate("/why-phone-first")} testid="profile-why" last />
      </div>

      <button onClick={doLogout} data-testid="profile-logout"
        className="tap-scale mt-4 flex w-full items-center justify-center gap-2 rounded-[24px] border border-black/5 bg-white py-4 font-semibold text-red-600">
        <LogOut className="w-5 h-5" /> Sign out
      </button>

      <p className="mt-6 text-center text-xs text-ho-muted">HandsOn · Built on a phone, for phones</p>
    </AppShell>
  );
}

const Row = ({ icon: Icon, label, onClick, testid, last }) => (
  <button onClick={onClick} data-testid={testid}
    className={`tap-scale flex w-full items-center gap-3 px-5 py-4 text-left ${last ? "" : "border-b border-black/5"}`}>
    <div className="grid place-items-center w-9 h-9 rounded-xl bg-black/5">
      <Icon className="w-4.5 h-4.5 text-ho-ink" />
    </div>
    <span className="flex-1 font-medium">{label}</span>
    <ChevronRight className="w-5 h-5 text-black/20" />
  </button>
);
